import { getAnalyticsEnvironment } from "@/lib/analytics/environment";
import { trackException } from "@/lib/tracking/appInsights";

type LogLevel = "debug" | "info" | "warn" | "error" | "fatal";

type LogAttributes = Record<string, unknown>;

type LogOptions = {
  attributes?: LogAttributes;
  error?: unknown;
  suppressTelemetry?: boolean;
};

type LoggerFn = (message: string, options?: LogOptions) => void;

type Logger = {
  debug: LoggerFn;
  info: LoggerFn;
  warn: LoggerFn;
  error: LoggerFn;
  fatal: LoggerFn;
  child: (scope: string, defaults?: LogAttributes) => Logger;
};

type LoggerInternalConfig = {
  scope: string;
  defaults: LogAttributes;
};

const environment = getAnalyticsEnvironment();

const mergeAttributes = (...groups: Array<LogAttributes | undefined>): LogAttributes => {
  const merged: LogAttributes = {};

  for (const group of groups) {
    if (!group) {
      continue;
    }

    for (const [key, value] of Object.entries(group)) {
      if (value === undefined) {
        delete merged[key];
        continue;
      }
      merged[key] = value;
    }
  }

  return merged;
};

const buildConsoleArgs = (message: string, attributes: LogAttributes, error: unknown): unknown[] => {
  const args: unknown[] = [message];

  if (Object.keys(attributes).length > 0) {
    args.push(attributes);
  }

  if (error !== undefined) {
    args.push(error);
  }

  return args;
};

const routeToConsole = (level: LogLevel, args: unknown[]) => {
  switch (level) {
    case "debug":
      console.debug(...args);
      return;
    case "info":
      console.info(...args);
      return;
    case "warn":
      console.warn(...args);
      return;
    case "error":
    case "fatal":
    default:
      console.error(...args);
      return;
  }
};

const emitTelemetry = (
  level: LogLevel,
  scope: string,
  message: string,
  attributes: LogAttributes,
  error: unknown,
  suppressTelemetry?: boolean,
) => {
  if (suppressTelemetry) {
    return;
  }

  if (level !== "error" && level !== "fatal") return;

  trackException(error ?? message, {
    level,
    source: scope,
    properties: attributes,
    suppressConsole: true,
  });
};

const logWithConfig =
  ({ scope, defaults }: LoggerInternalConfig) =>
  (level: LogLevel, message: string, options: LogOptions = {}) => {
    const attributes = mergeAttributes({ environment }, defaults, options.attributes);
    const error = options.error;

    const consoleArgs = buildConsoleArgs(`[${scope}] ${message}`, attributes, error);
    routeToConsole(level, consoleArgs);
    emitTelemetry(level, scope, message, attributes, error, options.suppressTelemetry);
  };

const createLoggerInternal = (config: LoggerInternalConfig): Logger => {
  const invoke = logWithConfig(config);

  const child = (scope: string, defaults: LogAttributes = {}): Logger => {
    const childScope = scope ? `${config.scope}.${scope}` : config.scope;
    return createLoggerInternal({
      scope: childScope,
      defaults: mergeAttributes(config.defaults, defaults),
    });
  };

  return {
    debug: (message, options) => invoke("debug", message, options),
    info: (message, options) => invoke("info", message, options),
    warn: (message, options) => invoke("warn", message, options),
    error: (message, options) => invoke("error", message, options),
    fatal: (message, options) => invoke("fatal", message, options),
    child,
  };
};

export const createLogger = (scope: string, defaults: LogAttributes = {}): Logger => {
  return createLoggerInternal({
    scope,
    defaults,
  });
};

export const logger = createLogger("frontend");
